import { Button } from '@timelyapp/react';
import type { DropdownOption } from 'components/DropdownListWithOptions';
import PopoverDropdownList from 'components/popovers/PopoverDropdownList';
import { isNull } from 'lodash-es';
import React from 'react';
import { useDispatch } from 'react-redux';
import type {
  GraphMetadata,
  WidgetOptions,
  WidgetWithData,
} from 'redux/modules/superreports.types';
import { updateWidgetOptions } from 'redux/modules/superreports.types';
import type { ThunkDispatch } from 'types/redux';
import styles from './TimelineWidget.module.scss';

const dataTypeOptions: Array<DropdownOption> = [
  {
    name: 'Logged hours',
    action: 'hours',
  },
  {
    name: 'Logged money',
    action: 'money',
  },
];

interface Props {
  billedState: boolean | null;
  isLoading: boolean;
  readOnly?: boolean;
  widget: WidgetWithData<any, GraphMetadata>;
}

const TimelineWidgetHeader: React.FC<Props> = ({ billedState, readOnly, widget, isLoading }) => {
  const dispatch = useDispatch<ThunkDispatch>();

  const handleChangeDataType = (option: DropdownOption) => {
    if (option.action === widget.options!.dataType) return;

    const updatedOptions: Partial<WidgetOptions> = {
      dataType: option.action as any,
    };

    if (widget.options?.showBillableNonBillable && option.action === 'money') {
      updatedOptions.showBillableNonBillable = false;
    }

    dispatch(
      updateWidgetOptions({
        reference: widget.id || widget._ref,
        options: updatedOptions,
      })
    );
  };

  const dataType =
    dataTypeOptions.find(option => option.action === widget.options!.dataType)?.name ?? '???';

  const showBillableNonBillable = !!widget.options?.showBillableNonBillable;
  const showBilledUnbilled =
    !showBillableNonBillable && isNull(billedState) && widget.metadata?.showBilledUnbilled;

  return (
    <>
      {!readOnly ? (
        <PopoverDropdownList
          options={dataTypeOptions}
          onClick={handleChangeDataType}
          target={
            <Button priority="low" className={styles.title}>
              {dataType}
            </Button>
          }
        />
      ) : (
        dataType
      )}
      <div className={styles.header} />
      <ul className={styles.legend}>
        {isLoading && (
          <>
            <li className={styles.headerLoadingPlaceholder} />
            <li className={styles.headerLoadingPlaceholder} />
            <li className={styles.headerLoadingPlaceholder} />
          </>
        )}
        {!isLoading && (
          <>
            {showBillableNonBillable && (
              <>
                <li className={styles.billable}>Billable</li>
                <li className={styles.nonBillable}>Non-billable</li>
              </>
            )}
            {showBilledUnbilled && (
              <>
                <li className={styles.billed}>Billed</li>
                <li className={styles.unbilled}>Unbilled</li>
              </>
            )}
            {widget.options?.showMedian && widget.metadata && (
              <li className={styles.median}>Median</li>
            )}
          </>
        )}
      </ul>
    </>
  );
};

export default TimelineWidgetHeader;
